import { PlusIcon } from '@heroicons/react/24/outline';
import { GQLProductColourFragment } from '@theprintbar/graphql';
import { cn, getSortedColours } from '@theprintbar/shared';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { ColourSwatch } from '../ColourSwatch';
import { LightningBolt } from '../Icons';
import { ColourDropdown } from './ColourDropdown';

export type CustomColourIconHandler = (
	colour: GQLProductColourFragment,
	colours: GQLProductColourFragment[],
	isActive: boolean,
) => React.ReactNode;

interface ColourSwatchProps {
	colours: GQLProductColourFragment[];
	currentSetCode?: string;
	hideLightningBolt?: boolean;
	samedayOnly?: boolean;
	hideNonSameday?: boolean;
	hideOnMobile?: boolean;
	hideName?: boolean;
	max?: number;
	renderAllOnMoreClick?: boolean;
	activeCode?: string;
	activeCodes?: string[];
	unavailableCodes?: string[];
	allowOOS?: boolean;
	onClick: (colour: GQLProductColourFragment) => void;
	onHover?: (colour?: GQLProductColourFragment) => void;
	className?: string;
	customColourIconHandler?: CustomColourIconHandler;
}

export function ProductColourSwatch({
	colours,
	samedayOnly,
	currentSetCode,
	hideOnMobile,
	hideNonSameday,
	hideLightningBolt,
	hideName,
	activeCode,
	activeCodes,
	unavailableCodes,
	allowOOS,
	onClick,
	onHover,
	className,
	max,
	renderAllOnMoreClick,
	customColourIconHandler,
}: ColourSwatchProps) {
	const [showAll, setShowAll] = useState(false);
	const sortedColours = getSortedColours(colours).filter(({ isSameday }) => !hideNonSameday || isSameday);
	const activeColours = sortedColours.filter(({ colour }) =>
		activeCodes?.length ? activeCodes.includes(colour.code) : colour.code === activeCode,
	);

	const realMax = showAll ? sortedColours.length : max || sortedColours.length;

	//Make sure selected colour is always visible
	const currentSetCodeIndex = sortedColours.findIndex(({ colour }) => colour.code === currentSetCode);
	if (currentSetCodeIndex > realMax) {
		//move it to the realMax index
		const colour = sortedColours[currentSetCodeIndex];
		sortedColours.splice(currentSetCodeIndex, 1);
		sortedColours.splice(realMax - 1, 0, colour);
	}

	const [hoveredColour, setHoveredColour] = useState<GQLProductColourFragment | undefined>(activeColours?.[0]);

	const Swatch = useCallback(
		(productColour: GQLProductColourFragment, className?: string) => {
			const { id, colour, inStock, isSameday } = productColour;
			const isActive = activeColours.some(pc => pc.colour.code === colour.code);
			const customIcon = customColourIconHandler?.(productColour, colours, isActive);
			const disabled =
				(!inStock && !allowOOS) || (samedayOnly && (!inStock || !isSameday)) || unavailableCodes?.includes(colour.code);
			return isSameday || !hideNonSameday ? (
				<button
					key={`swatch-${id}`}
					type="button"
					className={cn('group/swatch w-fit', disabled && 'opacity-30 cursor-default', className)}
					onClick={() => !disabled && onClick(productColour)}
					onMouseEnter={() => {
						setHoveredColour(productColour);
						onHover?.(productColour);
					}}
				>
					<div className="relative">
						<ColourSwatch colour={colour} className={cn('border-grey/50', isActive && 'border-blue')} />
						<div className="absolute -top-1 -right-1">
							{customIcon ? (
								customIcon
							) : !hideLightningBolt && inStock && isSameday ? (
								<LightningBolt
									className={cn('w-auto h-5 group-hover/swatch:fill-blue', isActive ? 'fill-blue' : 'fill-grey')}
								/>
							) : null}
						</div>
					</div>
				</button>
			) : null;
		},
		[activeCode, onClick, onHover],
	);

	const RenderDropdownColour = useMemo(() => {
		return sortedColours.slice(max, sortedColours.length).map(colour => {
			return colour ? Swatch(colour, 'pr-0 pb-0') : null;
		});
	}, [sortedColours, max, Swatch]);

	const canShowMore = sortedColours.length > realMax;

	return (
		<div
			className={clsx('h-fit', hideOnMobile && 'hidden lg:block', className)}
			onMouseLeave={() => {
				setHoveredColour(undefined);
				onHover?.();
			}}
		>
			<div className="flex flex-wrap gap-2.5 mb-1.5">
				{/* default displayed colours */}
				{sortedColours.slice(0, realMax).map(colour => {
					return !!colour ? Swatch(colour) : null;
				})}
				{/* + button to show the rest of colours */}
				{canShowMore && (
					<ColourDropdown
						customClick={renderAllOnMoreClick ? () => setShowAll(true) : undefined}
						colours={sortedColours}
						realMax={realMax}
					>
						<div className="border border-grey/50 bg-white-pearl rounded-xl w-60 flex flex-wrap py-2 px-2 gap-2 items-center place-items-center">
							{RenderDropdownColour}
						</div>
					</ColourDropdown>
				)}
			</div>
			{((canShowMore && renderAllOnMoreClick) || showAll) && (
				<div>
					<button onClick={() => setShowAll(!showAll)} className="h-full flex items-center mt-4 gap-1">
						<PlusIcon className="w-2.5 h-2.5 text-grey" />
						<p className="text-xs text-grey leading-none">Show {showAll ? 'fewer' : 'all'} colours</p>
					</button>
				</div>
			)}

			{!hideName && (
				<div className="h-5 justify-start items-center hidden lg:flex">
					{(hoveredColour || (!activeCodes && !!activeColours?.length)) && (
						<h6 className="text-grey text-xs">
							{(hoveredColour || activeColours?.[0])?.colour.name}
							{!(hoveredColour || activeColours?.[0]).inStock && ' - Out of stock'}
						</h6>
					)}
				</div>
			)}
		</div>
	);
}
